import { useCallback, useEffect, useState } from "react";
import {
    FINANCIAL_DOCUMENTS_PAGE_SIZE,
    useFinancialDocumentsQuery,
} from "@components/documents/useFinancialDocumentsQuery";
import { useScope } from "@state/scopeContext.lib";
import { FinancialDocument } from "@common/types/financialDocument";

interface MutationOptions {
    onError: () => void;
}

interface UseAccrualDocumentLinksParams {
    financialDocumentIds?: number[];
    onLinkDocuments?: (documentIds: number[], options: MutationOptions) => void;
    onUnlinkDocument?: (documentId: number, options: MutationOptions) => void;
}

export const useAccrualDocumentLinks = ({
    financialDocumentIds: initialDocumentIds,
    onLinkDocuments,
    onUnlinkDocument,
}: UseAccrualDocumentLinksParams) => {
    const { workspaceId } = useScope();
    const [documentIds, setDocumentIds] = useState<number[]>(
        initialDocumentIds ?? [],
    );

    useEffect(() => {
        setDocumentIds(initialDocumentIds ?? []);
    }, [initialDocumentIds]);

    const {
        data: financialDocumentsResponse,
        isPending,
        isError,
    } = useFinancialDocumentsQuery({
        queryParams: {
            filters: {
                financialDocumentIds: documentIds,
            },
            page: 0,
            limit: FINANCIAL_DOCUMENTS_PAGE_SIZE,
            workspaceId: workspaceId ?? "",
        },
        additionalParams: { enabled: documentIds.length > 0 },
    });

    const linkDocuments = useCallback(
        (ids: number[]) => {
            if (!onLinkDocuments || ids.length === 0) {
                return;
            }
            const previousIds = documentIds;
            setDocumentIds((current) => [...new Set([...current, ...ids])]);
            onLinkDocuments(ids, {
                onError: () => setDocumentIds(previousIds),
            });
        },
        [documentIds, onLinkDocuments],
    );

    const attachDocument = useCallback(
        (documentId: number) => linkDocuments([documentId]),
        [linkDocuments],
    );

    const handleDocumentsUpload = useCallback(
        async (uploadedDocuments: FinancialDocument[]) =>
            linkDocuments(uploadedDocuments.map((document) => document.id)),
        [linkDocuments],
    );

    const detachDocument = useCallback(
        (documentId: number) => {
            if (!onUnlinkDocument) {
                return;
            }
            const previousIds = documentIds;
            setDocumentIds((current) =>
                current.filter((id) => id !== documentId),
            );
            onUnlinkDocument(documentId, {
                onError: () => setDocumentIds(previousIds),
            });
        },
        [documentIds, onUnlinkDocument],
    );

    const attachedDocuments = (financialDocumentsResponse?.data ?? []).filter(
        (document) => documentIds.includes(document.id),
    );
    const hasDocumentIds = documentIds.length > 0;
    const hasUnresolvedDocuments =
        hasDocumentIds && attachedDocuments.length === 0;

    return {
        documentIds,
        attachedDocuments,
        isResolvingDocuments: hasUnresolvedDocuments && isPending,
        hasDocumentsLoadError: hasUnresolvedDocuments && isError,
        attachDocument,
        attachDocuments: linkDocuments,
        handleDocumentsUpload,
        detachDocument,
    };
};
